import { useEffect, useMemo, useRef, useState } from 'react';

import { collection, getDocs, onSnapshot, query, Timestamp, where } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { EXECUTION_STATE } from '@src/models/execution';
import { getCompletitionData } from '@src/packages/utils/functions/triggers';
import { useExecutionStatesRt } from '@src/packages/utils/hooks/useExecutionStatesRt';

import type { ActiveUserExecutionDetail, ComplianceByUser } from '../types';
import { buildComplianceByUser, getExecutionMemberIds } from './buildComplianceByUser';

const ACTIVE_WINDOW_HOURS = 24;
const TERMINAL_STATES: string[] = [
  EXECUTION_STATE.expired,
  EXECUTION_STATE.completed,
  EXECUTION_STATE.incomplete,
];

interface ExecutionMember {
  id: string;
}

interface ExecutionDoc {
  id: string;
  startAt?: Timestamp;
  endAt?: Timestamp;
  division?: { id: string };
  schedule?: {
    type?: string;
    name?: string;
    members?: {
      users?: ExecutionMember[];
      groups?: { users?: ExecutionMember[] } | Array<{ users?: ExecutionMember[] }>;
    };
  };
}

interface TriggerDoc {
  id?: string;
  name?: string;
  expectedExecutions?: number;
  finishedExecutions?: number;
}

interface UseExecutionComplianceReturn {
  complianceByUser: ComplianceByUser;
  detailsByUser: Record<string, ActiveUserExecutionDetail[]>;
  loading: boolean;
  error: Error | null;
}

/**
 * Per-user trigger compliance (finished/expected, bucketed by execution kind) and stalled
 * flag, for every currently active execution — schedule, task or checkpoint route — in the
 * selected divisions.
 *
 * Executions are fetched once per division-selection change; their triggers are realtime
 * listeners (one per non-terminal execution), and their `currentState` is a realtime
 * listener per execution against `execution_states` — the execution doc itself carries no
 * trustworthy state field, so this hook does not read one off it. See
 * docs/adr/0001-single-execution-state-vocabulary.md.
 */
export const useExecutionCompliance = (
  orgId: string,
  divisionIds: string[],
): UseExecutionComplianceReturn => {
  const [executions, setExecutions] = useState<ExecutionDoc[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [triggersByExecution, setTriggersByExecution] = useState<Record<string, TriggerDoc[]>>({});

  const divisionsKey = useMemo(() => [...divisionIds].sort().join(','), [divisionIds]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!orgId || divisionIds.length === 0) {
      setExecutions([]);
      setExecutionsLoading(false);
      setError(null);
      return;
    }

    setExecutionsLoading(true);
    setError(null);

    const windowStart = Timestamp.fromMillis(Date.now() - ACTIVE_WINDOW_HOURS * 60 * 60 * 1000);
    const fetches = divisionIds.map(async divId => {
      const snapshot = await getDocs(
        query(
          collection(
            db,
            FSCollections.ORGANIZATIONS,
            orgId,
            FSCollections.DIVISIONS,
            divId,
            FSCollections.EXECUTIONS,
          ),
          where('endAt', '>', windowStart),
        ),
      );
      return snapshot.docs.map(doc => ({ ...(doc.data() as ExecutionDoc), id: doc.id, division: { id: divId } }));
    });

    Promise.all(fetches)
      .then(results => {
        if (requestIdRef.current !== requestId) return;
        const now = Date.now();
        const active = results
          .flat()
          .filter(execution =>
            execution.startAt && execution.endAt
              ? execution.startAt.toMillis() < now && execution.endAt.toMillis() > now
              : false,
          );
        setExecutions(active);
        setExecutionsLoading(false);
      })
      .catch((fetchError: unknown) => {
        if (requestIdRef.current !== requestId) return;
        setExecutions([]);
        setExecutionsLoading(false);
        setError(fetchError instanceof Error ? fetchError : new Error(String(fetchError)));
      });
    // divisionsKey encodes the division list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, divisionsKey]);

  const executionRefs = useMemo(
    () =>
      executions.map(execution => ({ id: execution.id, divisionId: execution.division?.id ?? '' })),
    [executions],
  );

  /**
   * One listener per fetched execution (`allow list: if false` on `execution_states`, same
   * constraint `useExecutionStatesRt` documents). This is what the "stalled" flag and the
   * trigger-listening gate below both read.
   */
  const {
    statesByExecutionId,
    loaded: statesLoaded,
    error: statesError,
  } = useExecutionStatesRt(orgId, executionRefs);

  /**
   * `loaded` stays true across the render that first receives executions: the states hook
   * only flips it false in its effect, and that effect has not run yet. An id missing from
   * the map (`undefined`, not `null`) is the signal that this execution has not resolved.
   */
  const statesReady = useMemo(() => {
    if (!statesLoaded) return false;
    return executions.every(execution => {
      if (!execution.id || !execution.division?.id) return true;
      return statesByExecutionId[execution.id] !== undefined;
    });
  }, [statesLoaded, executions, statesByExecutionId]);

  const listenableExecutions = useMemo(() => {
    if (!statesReady) return [];
    return executions.filter(execution => {
      if (!execution.division?.id) return false;
      const currentState = statesByExecutionId[execution.id]?.currentState ?? '';
      return !TERMINAL_STATES.includes(currentState);
    });
  }, [statesReady, executions, statesByExecutionId]);

  const listenableKey = useMemo(
    () =>
      listenableExecutions
        .map(execution => execution.id)
        .sort()
        .join(','),
    [listenableExecutions],
  );

  const listenableRef = useRef(listenableExecutions);
  listenableRef.current = listenableExecutions;

  useEffect(() => {
    const listenable = listenableRef.current;
    const wanted = new Set(listenable.map(execution => execution.id));

    setTriggersByExecution(previous => {
      const previousIds = Object.keys(previous);
      const sameSet =
        previousIds.length === wanted.size && previousIds.every(id => wanted.has(id));
      if (sameSet) return previous;
      return Object.fromEntries(
        previousIds.filter(id => wanted.has(id)).map(id => [id, previous[id]]),
      );
    });

    let active = true;
    const unsubscribes = listenable.map(execution =>
      onSnapshot(
        collection(
          db,
          FSCollections.ORGANIZATIONS,
          orgId,
          FSCollections.DIVISIONS,
          execution.division?.id ?? '',
          FSCollections.EXECUTIONS,
          execution.id,
          FSCollections.TRIGGERS,
        ),
        snapshot => {
          if (!active) return;
          const triggers = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as TriggerDoc));
          setTriggersByExecution(prev => ({ ...prev, [execution.id]: triggers }));
        },
      ),
    );

    return () => {
      active = false;
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
    // listenableKey is the set of non-terminal ids. State ticks that stay in that set do
    // not resubscribe. The executions themselves are read from a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, listenableKey]);

  const complianceByUser = useMemo(
    () => buildComplianceByUser(executions, statesByExecutionId, triggersByExecution),
    [executions, statesByExecutionId, triggersByExecution],
  );

  const detailsByUser = useMemo(() => {
    const byUser: Record<string, ActiveUserExecutionDetail[]> = {};
    executions.forEach(execution => {
      const type = execution.schedule?.type;
      if (type !== 'schedule' && type !== 'task' && type !== 'checkpoint_route') return;
      const triggers = triggersByExecution[execution.id] ?? [];
      const completion = getCompletitionData(triggers);
      const detail: ActiveUserExecutionDetail = {
        id: execution.id,
        type,
        name: execution.schedule?.name || execution.id,
        finished: completion.finishedExecutions,
        expected: completion.expectedExecutions,
        triggers: triggers.map((trigger, index) => {
          const triggerCompletion = getCompletitionData([trigger]);
          return {
            id: trigger.id || `${execution.id}-${index}`,
            name: trigger.name || `#${index + 1}`,
            finished: triggerCompletion.finishedExecutions,
            expected: triggerCompletion.expectedExecutions,
          };
        }),
      };
      getExecutionMemberIds(execution).forEach(userId => {
        (byUser[userId] ??= []).push(detail);
      });
    });
    return byUser;
  }, [executions, triggersByExecution]);

  return { complianceByUser, detailsByUser, loading: executionsLoading, error: error ?? statesError };
};
